import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRIMARY = '#263078';
const PRIMARY_MID = '#3949ab';
const PRIMARY_LT = '#e8eaf6';
const GROUP_EVEN = '#ffffff';
const GROUP_ODD  = '#f7fafc';
const GROUP_BORDER = '#cbd5e1';
const ACCENT_GREEN = '#0d9488';
const ACCENT_RED   = '#e11d48';
const TEXT_PRIMARY = '#1e293b';
const TEXT_SECONDARY = '#64748b';

const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)', letterSpacing: '0.3px', textTransform: 'uppercase' };
const td  = { padding: '7px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const statusStyle = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'paid' || s === 'full paid') return { bg: '#dcfce7', color: '#15803d' };
  if (s.includes('partial'))             return { bg: '#fff3cd', color: '#b45309' };
  if (s.includes('unpaid') || s.includes('pending')) return { bg: '#fee2e2', color: '#be123c' };
  return { bg: '#f1f5f9', color: '#64748b' };
};

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const payTypeColor = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('cash'))   return { bg: '#ecfdf5', color: '#0d9488' };
  if (t.includes('cheque') || t.includes('check')) return { bg: '#eff6ff', color: '#1d4ed8' };
  if (t.includes('upi') || t.includes('online'))   return { bg: '#fefce8', color: '#a16207' };
  if (t.includes('neft') || t.includes('rtgs'))    return { bg: '#f5f3ff', color: '#6d28d9' };
  return { bg: '#f1f5f9', color: '#64748b' };
};

const PaymentsReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [fetched, setFetched]       = useState(false);
  const [search, setSearch]         = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'FinancialStatementsReportApi',
        `/PaymentPaidReport/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setData([]);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  // Group by Bill No so same bill appears once with multiple payment rows
  const grouped = useMemo(() => {
    const map = new Map();
    const filteredData = !search.trim() ? data : data.filter(r => {
      const q = search.toLowerCase();
      return (
        (r.SupplierName   || '').toLowerCase().includes(q) ||
        (r.PurchaseBillNo || '').toLowerCase().includes(q) ||
        (r.GSTNumber      || '').toLowerCase().includes(q) ||
        (r.PaymentStatus  || '').toLowerCase().includes(q) ||
        (r.PaymentTypeName|| '').toLowerCase().includes(q)
      );
    });

    filteredData.forEach(r => {
      const key = r.PurchaseBillNo || r.PaymentId || Math.random();
      if (!map.has(key)) {
        map.set(key, {
          SupplierName: r.SupplierName,
          GSTNumber: r.GSTNumber,
          PurchaseBillNo: r.PurchaseBillNo,
          PurchaseDate: r.PurchaseDate,
          DueDate: r.DueDate,
          TotalAmount: r.TotalAmount,
          TotalPaidAmount: r.TotalPaidAmount,
          BalanceAmount: r.BalanceAmount,
          PaymentStatus: r.PaymentStatus,
          payments: [],
        });
      }
      map.get(key).payments.push({
        PaymentId: r.PaymentId,
        PaymentNo: r.PaymentNo,
        PaymentDate: r.PaymentDate,
        PaidAmount: r.PaidAmount,
        PaymentTypeName: r.PaymentTypeName,
        ChequeNumber: r.ChequeNumber,
        ChequeDate: r.ChequeDate,
        PaymentStatus: r.PaymentStatus,
      });
    });

    return Array.from(map.values());
  }, [data, search]);

  // Totals based on unique bills (not duplicated per payment)
  const totalAmount  = grouped.reduce((s, g) => s + (parseFloat(g.TotalAmount)     || 0), 0);
  const totalPaid    = grouped.reduce((s, g) => s + (parseFloat(g.TotalPaidAmount)  || 0), 0);
  const totalBalance = grouped.reduce((s, g) => s + (parseFloat(g.BalanceAmount)    || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'PaymentsReport', 0);
    if (!grouped.length) return;
    const cols = ['#', 'Supplier', 'GST No', 'Bill No', 'Purchase Date', 'Due Date', 'Total Amt', 'Paid Amt', 'Balance', 'Pay No', 'Pay Date', 'Pay Type', 'Cheque No', 'Cheque Date', 'Status'];
    const rows = [];
    grouped.forEach((g, i) => {
      g.payments.forEach((p, pi) => {
        rows.push([
          pi === 0 ? i + 1 : '',
          pi === 0 ? g.SupplierName : '',
          pi === 0 ? (g.GSTNumber || '') : '',
          pi === 0 ? g.PurchaseBillNo : '',
          pi === 0 ? fmtDate(g.PurchaseDate) : '',
          pi === 0 ? fmtDate(g.DueDate) : '',
          pi === 0 ? (parseFloat(g.TotalAmount) || 0) : '',
          parseFloat(p.PaidAmount) || 0,
          pi === 0 ? (parseFloat(g.BalanceAmount) || 0) : '',
          `PMT-${p.PaymentNo}`,
          fmtDate(p.PaymentDate),
          p.PaymentTypeName || '',
          p.ChequeNumber || '',
          fmtDate(p.ChequeDate),
          pi === 0 ? (g.PaymentStatus || '') : '',
        ]);
      });
    });

    await exportStyledExcel({
      reportTitle: 'PAYMENTS REPORT',
      sheetName: 'Payments Report',
      fileName: `PaymentsReport_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      headers: cols,
      rows,
    });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 8, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading payments report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '4px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a237e 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Payments Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search supplier, bill..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!grouped.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 110px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 950 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: `linear-gradient(135deg, ${PRIMARY_MID} 0%, ${PRIMARY} 100%)`, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Supplier</th>
                <th style={th}>GST No</th>
                <th style={th}>Bill No</th>
                <th style={th}>Purchase Date</th>
                <th style={th}>Due Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Amt</th>
                <th style={{ ...th, textAlign: 'right' }}>Paid Amt</th>
                <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                <th style={th}>Pay No</th>
                <th style={th}>Pay Date</th>
                <th style={{ ...th, textAlign: 'center' }}>Pay Type</th>
                <th style={th}>Cheque No</th>
                <th style={th}>Cheque Date</th>
                <th style={{ ...th, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    No records found
                  </td>
                </tr>
              ) : (
                grouped.map((g, gi) => {
                  const ss = statusStyle(g.PaymentStatus);
                  const rowCount = g.payments.length;
                  const groupBg = gi % 2 === 0 ? GROUP_EVEN : GROUP_ODD;
                  return g.payments.map((p, pi) => {
                    const pc = payTypeColor(p.PaymentTypeName);
                    const isFirst = pi === 0;
                    const isLast  = pi === rowCount - 1;
                    return (
                      <tr key={`${g.PurchaseBillNo}-${p.PaymentId || pi}`} style={{ background: groupBg, borderBottom: isLast ? `2px solid ${GROUP_BORDER}` : '1px solid #f1f5f9' }}>
                        {isFirst && (
                          <>
                            <td style={{ ...td, verticalAlign: 'middle', color: TEXT_SECONDARY, fontWeight: 500 }} rowSpan={rowCount}>{gi + 1}</td>
                            <td style={{ ...td, fontWeight: 600, color: TEXT_PRIMARY, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }} rowSpan={rowCount}>{g.SupplierName}</td>
                            <td style={{ ...td, color: TEXT_SECONDARY, fontSize: 11, verticalAlign: 'middle' }} rowSpan={rowCount}>{g.GSTNumber || '-'}</td>
                            <td style={{ ...td, color: PRIMARY, fontWeight: 600, verticalAlign: 'middle' }} rowSpan={rowCount}>{g.PurchaseBillNo}</td>
                            <td style={{ ...td, color: TEXT_PRIMARY, verticalAlign: 'middle' }} rowSpan={rowCount}>{fmtDate(g.PurchaseDate)}</td>
                            <td style={{ ...tdC, verticalAlign: 'middle' }} rowSpan={rowCount}>
                              {(() => {
                                const s = (g.PaymentStatus || '').toLowerCase();
                                const isPartial = s.includes('partial');
                                const isUnpaid  = s.includes('unpaid') || s.includes('pending');
                                const bg  = isUnpaid ? '#fee2e2' : isPartial ? '#fff3cd' : '#dcfce7';
                                const clr = isUnpaid ? '#be123c' : isPartial ? '#b45309' : '#15803d';
                                return (
                                  <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: bg, color: clr, whiteSpace: 'nowrap', display: 'inline-block' }}>
                                    {fmtDate(g.DueDate)}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={{ ...tdR, fontWeight: 600, color: TEXT_PRIMARY, verticalAlign: 'middle' }} rowSpan={rowCount}>{fmt(g.TotalAmount)}</td>
                          </>
                        )}
                        <td style={{ ...tdR, color: ACCENT_GREEN, fontWeight: 600 }}>{fmt(p.PaidAmount)}</td>
                        {isFirst && (
                          <td style={{ ...tdR, color: (parseFloat(g.BalanceAmount) || 0) > 0 ? ACCENT_RED : TEXT_PRIMARY, fontWeight: 600, verticalAlign: 'middle' }} rowSpan={rowCount}>{fmt(g.BalanceAmount)}</td>
                        )}
                        <td style={{ ...td, color: PRIMARY, fontWeight: 600 }}>PMT-{p.PaymentNo}</td>
                        <td style={{ ...td, color: TEXT_PRIMARY }}>{fmtDate(p.PaymentDate)}</td>
                        <td style={tdC}>
                          <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: pc.bg, color: pc.color }}>
                            {p.PaymentTypeName || '-'}
                          </span>
                        </td>
                        <td style={{ ...td, color: TEXT_SECONDARY, fontSize: 11 }}>{p.ChequeNumber || '-'}</td>
                        <td style={{ ...td, color: TEXT_SECONDARY, fontSize: 11 }}>{fmtDate(p.ChequeDate)}</td>
                        {isFirst && (
                          <td style={{ ...tdC, verticalAlign: 'middle' }} rowSpan={rowCount}>
                            <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: ss.bg, color: ss.color }}>
                              {g.PaymentStatus || '-'}
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  });
                })
              )}
            </tbody>
            {grouped.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f0f7ff', color: PRIMARY, fontWeight: 700, borderTop: `2px solid ${PRIMARY}` }}>
                  <td colSpan={6} style={{ ...td, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>Totals</td>
                  <td style={{ ...tdR, fontWeight: 700, fontSize: 12 }}>{fmt(totalAmount)}</td>
                  <td style={{ ...tdR, fontWeight: 700, fontSize: 12, color: ACCENT_GREEN }}>{fmt(totalPaid)}</td>
                  <td style={{ ...tdR, fontWeight: 700, fontSize: 12, color: totalBalance > 0 ? ACCENT_RED : TEXT_PRIMARY }}>{fmt(totalBalance)}</td>
                  <td colSpan={6} style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentsReport;




